import { graphql } from 'react-relay';
import React, { memo, useContext, useState, useCallback, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types/emitter';
import { AnnotationMarkStates, AnnotationTypes } from '@atlaskit/adf-schema/annotation';
import type { AddMarkStep } from '@atlaskit/editor-prosemirror/transform';
import { token } from '@atlaskit/tokens';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { MutationUpdaterInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { fg } from '@confluence/feature-gating';
import { isCanvasContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
// CommentType comes from comments-data, not comments-data-manager: consumers that have
// not collapsed the gate yet fully mock commentDataManagerUtils in their tests, which
// strips the enum at runtime. Both enums are identical ('inline' / 'general').
// Move once COMMENTS-7434 / COMMENTS-7436 land.
import {
	useCommentsData,
	CommentType,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import {
	type CommentData,
	type ReplyData,
	resolveCommentInCacheCallback,
	reopenCommentInCacheCallback,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import {
	useAllCommentsData,
	type CommentFooterLocation,
	type Comment as TopLevelComment,
	type CommentInlineLocation as CommentLocation,
	type CommentInlineResolveProperties,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { useUpdateCommentInCache } from '@confluence/comments-data-manager/entry-points/useUpdateCommentInCache.ts';
import { useProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useCommentsContentActions } from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { useUpdateDocument } from '@confluence/comment-context/entry-points/useUpdateDocument.ts';
import { InlineCommentFramework } from '@confluence/inline-comments-common/entry-points/InlineCommentFramework.ts';
import { EditComment } from '@confluence/inline-comments-common/entry-points/EditComment.ts';
import {
	CommentBody,
	drawsThreadRail,
} from '@confluence/inline-comments-common/entry-points/CommentBody.ts';
import { WithCurvedBranchingStyle } from '@confluence/inline-comments-common/entry-points/components.ts';
import { AvatarWithActionButtonAsync } from '@confluence/agent-mention/entry-points/AvatarWithActionButtonAsync.ts';
import { CommentAgentOpenChatMenuItemAsync } from '@confluence/agent-mention/entry-points/CommentAgentOpenChatMenuItemAsync.ts';
import { useAgentSessionsContext } from '@confluence/agent-mention/entry-points/AgentSessionsContext.ts';
import { useCommentAgentSession } from '@confluence/agent-mention/entry-points/useCommentAgentSession.ts';
import { OPTIMISTIC_AGENT_REPLY_ID_PREFIX } from '@confluence/agent-mention/entry-points/agentMentionConstants.ts';
import {
	defaultReactionsSummary,
	handleResolveSuccess,
	handleReopenSuccess,
	handleDeleteSuccess,
	handleMutationFailure,
	insertReopenedCommentAnnotation,
	getTranslatedError,
	isAlreadyDeletedError,
	parseError,
	resolveInlineCommentAnnotation,
	unresolveInlineCommentAnnotation,
} from '@confluence/inline-comments-common/entry-points/inlineCommentsUtils.ts';
import {
	hasContributors,
	type CommentAction,
} from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes.ts';
import { usePageContentId, useContentType } from '@confluence/page-context';
import { CommentDeletionLocation } from '@confluence/inline-comments-queries/entry-points/DeleteInlineCommentMutation.ts';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams.ts';
import type { InlineCommentAuthorKnownUser } from '@confluence/inline-comments-queries/entry-points/InlineCommentsQuery.ts';
import { useEditorAnnotations } from '@confluence/inline-comments-hooks/entry-points/useEditorAnnotations.ts';
import { useInlineCommentsActions } from '@confluence/inline-comments-hooks/entry-points/useInlineComments.ts';
import {
	RESOLVE_INLINE_COMMENT_EXPERIENCE,
	RESOLVE_PAGE_COMMENT_EXPERIENCE,
	DELETE_INLINE_COMMENT_EXPERIENCE,
	DELETE_PAGE_COMMENT_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import type { PageInfoNode } from '@confluence/page-info/entry-points/PageInfoQuery.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useRendererActions } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';
import {
	useAnnotationsIsOffline,
	useAnnotationsDispatch,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n.ts';
import { i18n as commentsI18n } from '@confluence/inline-comments-common/entry-points/i18n.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsNewDanglingCommentExperienceEnabled } from '@confluence/comments-panel-utils/entry-points/useIsNewDanglingCommentExperienceEnabled.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import {
	handleCommentsPanelNavigationWithViewChange,
	ScrollBehavior,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { buildMutationContextForNoNetworkError } from '@confluence/comments-util/entry-points/errorUtils.ts';
import { constructStepForGql } from '@confluence/comments-util/entry-points/rendererAnnotations.ts';
import {
	ViewValues,
	useCommentsPanel,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { useCommentService } from '@confluence/content-types-bridge-comments';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useRefetchReactionsOnAgentSessionChange } from '@confluence/agent-mention/entry-points/useRefetchReactionsOnAgentSessionChange.ts';
import type { fetchCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';

import { useShowCommentsPanel } from '../useShowCommentsPanel.ts';
import { getReplyToContributorUsers } from '../helper/commentThreadHelper.tsx';
import { arePropsShallowEqual } from '../helper/arePropsShallowEqual.ts';

import type { CommentResolveCommentMutation_Mutation as CommentResolveCommentMutation_Mutation$data } from './__types__/CommentResolveCommentMutation.ts';
import type { CommentReopenCommentMutation as CommentReopenCommentMutation_Mutation$data } from './__types__/CommentReopenCommentMutation.ts';
import type {
	CommentDeleteInlineCommentMutationWithStepMutation$data,
	CommentDeleteInlineCommentMutationWithStepMutation$variables,
} from './__generated__/CommentDeleteInlineCommentMutationWithStepMutation.graphql.ts';
import type {
	CommentDeleteInlineCommentMutation$data,
	CommentDeleteInlineCommentMutation$variables,
} from './__generated__/CommentDeleteInlineCommentMutation.graphql.ts';
import type {
	CommentDeleteGeneralCommentMutation$data,
	CommentDeleteGeneralCommentMutation$variables,
} from './__generated__/CommentDeleteGeneralCommentMutation.graphql.ts';

const CommentResolveCommentMutation = graphql`
	mutation CommentResolveCommentMutation($commentIds: [ID]!, $cloudId: ID!) {
		confluence_resolveComments(commentIds: $commentIds, cloudId: $cloudId) {
			success
			commentResolutionStates {
				commentId
				resolveProperties {
					resolvedTime
					resolvedUser
					resolvedFriendlyDate
				}
				status
			}
			errors {
				message
				extensions {
					... on ResolveCommentsMutationErrorExtension {
						errorType
						statusCode
					}
					__typename
				}
			}
		}
	}
`;
const CommentReopenCommentMutation = graphql`
	mutation CommentReopenCommentMutation($commentId: ID!, $cloudId: ID!) {
		confluence_reopenComment(commentId: $commentId, cloudId: $cloudId) {
			success
			commentResolutionStates {
				commentId
				status
			}
			errors {
				message
				extensions {
					... on ReopenCommentsMutationErrorExtension {
						errorType
						statusCode
					}
					__typename
				}
			}
		}
	}
`;
const CommentDeleteInlineCommentMutationWithStepMutation = graphql`
	mutation CommentDeleteInlineCommentMutationWithStepMutation($input: DeleteInlineCommentInput!) {
		deleteInlineComment(input: $input)
	}
`;
const CommentDeleteInlineCommentMutation = graphql`
	mutation CommentDeleteInlineCommentMutation(
		$commentIdToDelete: ID!
		$deleteFrom: CommentDeletionLocation
	) {
		deleteComment(commentIdToDelete: $commentIdToDelete, deleteFrom: $deleteFrom)
	}
`;
const CommentDeleteGeneralCommentMutation = graphql`
	mutation CommentDeleteGeneralCommentMutation(
		$commentIdToDelete: ID!
		$deleteFrom: CommentDeletionLocation
	) {
		deleteComment(commentIdToDelete: $commentIdToDelete, deleteFrom: $deleteFrom)
	}
`;
type CommentProps = {
	comment: CommentData | ReplyData;
	supportedTopLevelActions: CommentAction[];
	pageMode: PageMode;
	pageInfo: PageInfoNode | null;
	isUnread?: boolean;
	threadKey: string;
	flags?: FlagsStateContainer;
	isAnnotatedTextPressed?: boolean;
	setAnnotatedTextPressed?: (state: boolean) => void;
	adjustForNestedReplies?: boolean;
	hideBranchingStyle?: boolean;
	canAddComments?: boolean;
	isParentCommentOpen?: boolean;
	/** For replies: the parent thread's highlighted text has been deleted from the page. */
	isParentCommentDangling?: boolean;
	isStandaloneComment?: boolean;
	showRepliesWithCurvedBorders?: boolean;
	deleteStandaloneComment?: (parentCommentId: string, commentId: string) => void;
	replyToComment?: (commentId: string, commentData: ReplyData) => void;
	updateReactionsCacheFn?: (
		emojiId: string,
		actionType: 'add' | 'delete',
		contentId: string,
		containerId?: string,
	) => MutationUpdaterInterop<any> | void;
	onRendered?: () => void;
	isLastUnnestableReply?: boolean;
	/**
	 * True when the thread's reply editor is open below the last reply. Lets the last
	 * reply extend its connector line over its agent-session status row to reach the
	 * editor (otherwise the line stops short of the editor, leaving a visual gap).
	 */
	isReplyEditorOpen?: boolean;
	commentHandlesPadding?: boolean;
	hasParentMaxHeight?: boolean;
	hideReactions?: boolean;
};

const i18n = defineMessages({
	commentReopenedFlagTitle: {
		id: 'comments-panel.comment.reopened.flag.title',
		defaultMessage: 'Comment reopened',
		description:
			'Title to display in Flag to inform user when the comment was successfully reopened.',
	},
	commentReopenFailedFlagTitle: {
		id: 'comments-panel.comment.reopen.failed.flag.title',
		defaultMessage: 'Failed to reopen comment',
		description: 'Title to display in Flag to inform user when the comment failed to reopen.',
	},
	commentReopenCacheUpdateFailedFlagTitle: {
		id: 'comments-panel.comment.reopen.cache-update.failed.flag.title',
		defaultMessage: 'Failed to update reopened comment. Please refresh the page.',
		description:
			'Title to display in Flag to inform user when the comment was reopened but the local cache update failed.',
	},
	retryCTA: {
		id: 'comments-panel.comment.reopen.failed.flag.cta.retry',
		defaultMessage: 'Retry now',
		description:
			'CTA button text displayed in Error Flag for users to retry the failed comment reopen action.',
	},
});

const commentWrapperReplyPaddingOverrides = css({
	paddingLeft: token('space.200'),
	paddingBottom: token('space.100'),
});

const standaloneCommentWrapperReplyPaddingOverrides = css({
	marginLeft: token('space.150'),
});

const leftBorderStyle = css({
	borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
});

const leftBorderStyleUpdated = css({
	borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
});

// this component can be used for parent comment and a reply
const CommentComponent = ({
	comment,
	supportedTopLevelActions,
	pageMode,
	pageInfo,
	isUnread = false,
	threadKey,
	flags,
	isAnnotatedTextPressed,
	setAnnotatedTextPressed,
	adjustForNestedReplies = false,
	hideBranchingStyle = false,
	hideReactions = false,
	canAddComments = true,
	isParentCommentOpen,
	isParentCommentDangling,
	isStandaloneComment,
	showRepliesWithCurvedBorders,
	deleteStandaloneComment,
	replyToComment,
	updateReactionsCacheFn,
	onRendered,
	isLastUnnestableReply = false,
	isReplyEditorOpen = false,
	commentHandlesPadding = false,
	hasParentMaxHeight = false,
}: CommentProps): React.JSX.Element => {
	// Store stable references to break circular dependency in useCallback
	const handleResolveCommentRef = useRef<(() => void) | null>(null);
	const handleReopenCommentRef = useRef<(() => void) | null>(null);
	const [editCommentId, setCommentForEdit] = useState('');
	const { commentService } = useCommentService();
	const isNewDanglingCommentExperienceEnable = useIsNewDanglingCommentExperienceEnabled();

	const isOffline = useAnnotationsIsOffline();
	const { setEditorViewProps } = useAnnotationsDispatch();

	const experienceTracker = useContext(ExperienceTrackerContext);
	const { resetContentChanged } = useCommentsContentActions();
	const [contentId = ''] = usePageContentId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { removeUnresolvedInlineComment, addUnresolvedInlineComment } = useInlineCommentsActions();
	const [{ orderedActiveAnnotationIdList }, { setOrderedActiveAnnotationIdList }] =
		useCommentsData();

	// Get cache operations
	const { deleteCommentInCache } = useAllCommentsData({
		contentId,
		options: {
			skip: true, // Only using write operations, no need to fetch data
		},
	});

	// Get processed comments data
	const { commentsDataMap } = useProcessedCommentsDataState();
	const [{ annotations }] = useEditorAnnotations();
	const { rendererActions } = useRendererActions();
	const [{ updateDocumentFn }] = useUpdateDocument();
	const { cloudId } = useSessionData();

	const [
		{ currentlySelectedThreadKey },
		{ setCurrentView, setCurrentlySelectedThreadKey, setReplyToCommentId },
	] = useCommentsPanel();
	const { formatMessage } = useIntl();
	const { focusedCommentId } = useInlineCommentQueryParams();
	const { isCommentsPanelShown, showCommentsPanel } = useShowCommentsPanel();
	const [contentType] = useContentType();

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const [resolveCommentFn] = useMutationInterop<CommentResolveCommentMutation_Mutation$data>(
		CommentResolveCommentMutation,
		buildMutationContextForNoNetworkError(),
	);

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const [reopenCommentFn] = useMutationInterop<CommentReopenCommentMutation_Mutation$data>(
		CommentReopenCommentMutation,
		{
			refetchQueries: [],
			...buildMutationContextForNoNetworkError(),
		},
	);

	const [deleteTopLevelRendererInlineCommentFn] = useMutationInterop<
		CommentDeleteInlineCommentMutationWithStepMutation$data,
		CommentDeleteInlineCommentMutationWithStepMutation$variables
	>(CommentDeleteInlineCommentMutationWithStepMutation, buildMutationContextForNoNetworkError());

	const [deleteInlineCommentFn] = useMutationInterop<
		CommentDeleteInlineCommentMutation$data,
		CommentDeleteInlineCommentMutation$variables
	>(CommentDeleteInlineCommentMutation, buildMutationContextForNoNetworkError());

	const [deleteGeneralCommentFn] = useMutationInterop<
		CommentDeleteGeneralCommentMutation$data,
		CommentDeleteGeneralCommentMutation$variables
	>(CommentDeleteGeneralCommentMutation, buildMutationContextForNoNetworkError());
	// Get the update functions for the comments data manager - aliased to support the different
	// mutation's different result types
	const { updateCommentInCache: updateResolveCommentInCache } =
		useUpdateCommentInCache<CommentResolveCommentMutation_Mutation$data>();
	const { updateCommentInCache: updateReopenCommentInCache } =
		useUpdateCommentInCache<CommentReopenCommentMutation_Mutation$data>();

	const eventEmitter =
		pageMode === PageMode.VIEW
			? getRendererAnnotationEventEmitter()
			: getEditorAnnotationEventEmitter();

	const userId = (comment?.author as InlineCommentAuthorKnownUser)?.accountId;
	const topCommentUserAvatar = comment?.author?.profilePicture?.path;
	const topCommentDisplayName =
		comment?.author?.displayName ?? formatMessage(commentsi18n.anonymous);
	const commentId = comment?.id;
	const parentReactionsSummary = comment?.reactionsSummary ?? null;
	const [refetchedReaction, setRefetchedReaction] = useState<{
		commentId: string;
		parentSummary: typeof parentReactionsSummary;
		summary: NonNullable<Awaited<ReturnType<typeof fetchCommentById>>>['reactionsSummary'];
	} | null>(null);
	const displayedReactionsSummary =
		refetchedReaction &&
		refetchedReaction.commentId === commentId &&
		refetchedReaction.parentSummary === parentReactionsSummary
			? refetchedReaction.summary
			: parentReactionsSummary;
	const handleReactionRefetch = useCallback(
		(refetchedComment: Awaited<ReturnType<typeof fetchCommentById>>) => {
			if (refetchedComment?.id && refetchedComment.id === commentId) {
				setRefetchedReaction({
					commentId: refetchedComment.id,
					parentSummary: parentReactionsSummary,
					summary: refetchedComment.reactionsSummary,
				});
			}
		},
		[commentId, parentReactionsSummary],
	);
	const date = comment?.version?.when;
	const createdAt = comment?.createdAt?.value;
	const createdAtNonLocalized = comment?.createdAtNonLocalized;
	const versionByDisplayName = comment?.version?.by?.displayName;
	const versionDate = comment?.version?.when;
	const permissions = comment?.permissions;
	const content = comment?.body?.value;
	const permissionType = comment?.author?.permissionType ?? undefined;
	const parentCommentId = comment?.parentId;
	const isReply = !!parentCommentId;
	const isEdited = (comment?.version?.number || 0) > 1;
	const numReplies = (comment as TopLevelComment)?.replies?.length || 0;

	// Agent session — wire SSE state for the agent-mention-in-comment feature.
	// Any comment (top-level OR reply) can be associated with an agent session, so
	// each comment looks up its OWN session by commentId from AgentSessionsContext
	// (populated by the per-surface AgentSessionsFetcher mounted in CommentsPanelList).
	// When there is no session the hook is a no-op. The experiment is inlined per
	// confluence-feature-gating/inline-usage and ordered last (gate-last).
	const agentSessions = useAgentSessionsContext();
	const agentSession = commentId ? agentSessions.get(commentId) : undefined;
	const {
		state: agentSessionState,
		agentIdentity: optimisticAgentIdentity,
		sessionStatusRef,
	} = useCommentAgentSession(
		expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true) ? (commentId ?? '') : '',
		expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)
			? agentSession?.conversationId
			: undefined,
		expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)
			? (agentSession?.state ?? undefined)
			: undefined,
	);

	// The agent writes/removes its own reaction on the backend as it works (Reaction Service),
	// with no pubsub reaching the client for it. Piggyback on the same session-state signal that
	// drives the status row above to refetch this comment's reactions on each transition.
	useRefetchReactionsOnAgentSessionChange({
		commentId,
		contentId,
		agentSessionState,
		displayedReactionsSummary,
		onRefetch: handleReactionRefetch,
	});

	const commentUrl = pageMode === PageMode.EDIT ? comment?.links?.editui : comment?.links?.webui; // view page and live docs uses the webui field
	const isDatabaseOrWhiteboard = contentType === 'database' || isCanvasContentType(contentType);

	// When hideReactions (e.g. comment preview popup), pass empty summary so reactions query is skipped and none render
	const inheritedReactionsData = hideReactions
		? defaultReactionsSummary
		: displayedReactionsSummary;
	const contributors = hasContributors(comment) ? comment.contributors : undefined;

	const inlineCommentlocation = comment?.location as CommentLocation;
	const footerCommentLocation = comment?.location as CommentFooterLocation;

	const commentType =
		inlineCommentlocation?.type === 'INLINE' ? CommentType.INLINE : CommentType.GENERAL;

	const getResolvedProperties = useCallback(
		(type: CommentType) => {
			const properties =
				type === CommentType.INLINE
					? inlineCommentlocation?.inlineResolveProperties
					: footerCommentLocation?.commentResolveProperties;
			if (!isReply) {
				return properties || undefined;
			}
			return undefined;
		},
		[inlineCommentlocation, footerCommentLocation, isReply],
	);

	const resolveProperties = getResolvedProperties(commentType);
	const isThreadRootDangling = isReply
		? isParentCommentDangling
		: inlineCommentlocation?.inlineResolveProperties?.isDangling;

	const annotatedText = inlineCommentlocation?.inlineText || undefined;

	const deleteCommentInCacheOnUpdate = useCallback(
		(removalCommentId: string, experienceName: string) =>
			deleteCommentInCache({
				commentId: removalCommentId,
				onError: (err) => {
					void handleMutationFailure({
						experienceTracker,
						experienceName,
						error: err,
						commentId: removalCommentId,
					});
				},
			}),
		[deleteCommentInCache, experienceTracker],
	);

	// This used to be behind confluence_frontend_content_wrapper, but that gate is rolled out.
	// Keeping this as a single variable keeps experiment exposures constant, and the `|| false`
	// avoids feature-gating ratcheting concerns until confluence_fe_delete_comment_optimistic is cleaned up.
	const isOptimisticDeleteEnabled =
		expVal('confluence_fe_delete_comment_optimistic', 'isEnabled', false) || false;

	const handleDeleteInlineComment = () => {
		// NOTE: The DELETE experience is started in CommentActions.tsx
		// Replies carry no resolve properties of their own (see getResolvedProperties), so take the
		// thread's detachment from the parent for them.
		const isDangling = isReply
			? isParentCommentDangling
			: inlineCommentlocation?.inlineResolveProperties?.isDangling;
		const isTopLevelRendererComment =
			!isDatabaseOrWhiteboard && pageMode === PageMode.VIEW && !isReply;

		const canDeleteAnnotation = !isDangling && isTopLevelRendererComment;

		let step: AddMarkStep | undefined;

		if (canDeleteAnnotation) {
			try {
				const deleteResult = rendererActions?.deleteAnnotation(threadKey, 'inlineComment');
				if (deleteResult) {
					step = deleteResult.step as AddMarkStep;
				} else {
					experienceTracker.stopOnError({
						name: DELETE_INLINE_COMMENT_EXPERIENCE,
						error: new Error(`Unable to remove annotation ${threadKey} from document`),
					});
					return;
				}
			} catch (e) {
				experienceTracker.stopOnError({
					name: DELETE_INLINE_COMMENT_EXPERIENCE,
					error: e,
				});
				return;
			}
		}
		if (isStandaloneComment) {
			deleteStandaloneComment &&
				parentCommentId &&
				deleteStandaloneComment(parentCommentId, commentId);
			return;
		}

		resetContentChanged();

		// Optimistically mark the annotation as resolved.
		// If deletion is successful, the annotation will be deleted later;
		// if deletion fails, the annotation will be unresolved.
		if (!isReply && isOptimisticDeleteEnabled) {
			resolveInlineCommentAnnotation(threadKey, pageMode, eventEmitter);
		}
		const deleteMutation = canDeleteAnnotation
			? deleteTopLevelRendererInlineCommentFn
			: deleteInlineCommentFn;
		const optimisticDeleteResponse = canDeleteAnnotation
			? { deleteInlineComment: true }
			: { deleteComment: true };
		const deleteVariables = {
			variables: canDeleteAnnotation
				? {
						input: {
							commentId,
							step: step ? constructStepForGql(step) : {},
						},
					}
				: {
						commentIdToDelete: commentId,
						// There is no VIEW option
						deleteFrom:
							pageMode === PageMode.EDIT
								? CommentDeletionLocation.EDITOR
								: CommentDeletionLocation.LIVE,
					},
			update: () => deleteCommentInCacheOnUpdate(commentId, DELETE_INLINE_COMMENT_EXPERIENCE),
			optimisticResponse: isOptimisticDeleteEnabled ? optimisticDeleteResponse : undefined,
		};

		deleteMutation({
			...deleteVariables,
		} as any)
			.then(({ data }: { data?: any }) => {
				if (
					!data ||
					(canDeleteAnnotation && !data.deleteInlineComment) ||
					(!canDeleteAnnotation && !data.deleteComment)
				) {
					throw new Error('No data returned from mutation');
				}

				const deleteAnnotationAndCleanUpParent = (annotationId: string) => {
					// For a top level comment in renderer, we need to send out the events to tell the
					// renderer to deselect the annotation and remove it from the document
					if (pageMode === PageMode.VIEW) {
						const deleteResult = rendererActions?.deleteAnnotation(annotationId, 'inlineComment');

						if (deleteResult && deleteResult.doc) {
							// Inform the Renderer to de-select the annotation
							eventEmitter.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
							eventEmitter.emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
							updateDocumentFn && updateDocumentFn(deleteResult.doc);
						}

						// For editor/live pages we need to send the event to delete the mark
					} else {
						eventEmitter.emit('delete', annotationId);
					}
				};

				// send bridge api signal to content types.
				commentService.onDeleteComment?.(threadKey);

				const onSuccess = (annotationId: string) => {
					if (!isReply) {
						deleteAnnotationAndCleanUpParent(annotationId);
					}

					experienceTracker.succeed({
						name: DELETE_INLINE_COMMENT_EXPERIENCE,
					});
				};

				handleDeleteSuccess({
					commentId,
					annotationId: threadKey,
					pageId: contentId,
					pageMode,
					isReply,
					source: isStandaloneComment ? AnalyticsSource.STANDALONE : AnalyticsSource.COMMENTS_PANEL,
					inlineNodeTypes: undefined, // We need to figure out how to get this data
					createAnalyticsEvent,
					removeUnresolvedInlineComment,
					onSuccess,
					isDangling,
				});
			})
			.catch((err) => {
				handleDeleteCommentError({
					err,
				});
			});
	};

	const handleDeleteGeneralComment = () => {
		let deleteLocation: CommentDeletionLocation | undefined;

		// There is no deleteLocation for View Page
		if (pageMode !== PageMode.VIEW) {
			deleteLocation =
				pageMode === PageMode.EDIT ? CommentDeletionLocation.EDITOR : CommentDeletionLocation.LIVE;
		}

		const deleteVariables = {
			variables: {
				commentIdToDelete: commentId,
				deleteFrom: deleteLocation,
			},
			update: () => deleteCommentInCacheOnUpdate(commentId, DELETE_PAGE_COMMENT_EXPERIENCE),
			optimisticResponse: isOptimisticDeleteEnabled ? { deleteComment: true } : undefined,
		};

		deleteGeneralCommentFn({ ...deleteVariables })
			.then(({ data }) => {
				if (!data || !data.deleteComment) {
					throw new Error('No data returned from mutation');
				}

				resetContentChanged();

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'deleted',
						actionSubject: 'comment',
						actionSubjectId: commentId,
						objectType: 'page',
						objectId: contentId,
						source: AnalyticsSource.COMMENTS_PANEL,
						attributes: {
							isReply,
							commentType: CommentType.GENERAL,
							source: isStandaloneComment
								? AnalyticsSource.STANDALONE
								: AnalyticsSource.COMMENTS_PANEL,
							pageMode,
						},
					},
				}).fire();

				experienceTracker.succeed({
					name: DELETE_PAGE_COMMENT_EXPERIENCE,
				});
			})
			.catch((err) => {
				handleDeleteCommentError({
					err,
				});
			});
	};

	const handleDeleteCommentError = ({ err }: { err: Error }) => {
		const isInlineComment = commentType === CommentType.INLINE;

		const parsedError = parseError(err);
		const translatedError = getTranslatedError(parsedError.message, commentId);

		if (isAlreadyDeletedError(translatedError)) {
			void flags?.showWarningFlag({
				id: commentId,
				title: <FormattedMessage {...commentsI18n.commentAlreadyDeletedTitle} />,
				description: <FormattedMessage {...translatedError} />,
			});

			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'alreadyDeleted',
					actionSubject: 'comment',
					actionSubjectId: commentId,
					objectType: 'page',
					objectId: contentId,
					source: AnalyticsSource.COMMENTS_PANEL,
					attributes: {
						commentType,
						mode: pageMode,
						framework: isInlineComment ? InlineCommentFramework.ANNOTATION_PROVIDER : undefined,
						isReply,
					},
				},
			}).fire();
		} else if (isOptimisticDeleteEnabled) {
			// show an error flag to the user when optimistic deletion fails
			void flags?.showErrorFlag({
				id: commentId,
				title: <FormattedMessage {...commentsI18n.deleteErrorTitle} />,
				description: <FormattedMessage {...commentsI18n.deleteError} />,
			});
		}

		if (isInlineComment && !isReply && isOptimisticDeleteEnabled) {
			unresolveInlineCommentAnnotation(threadKey, pageMode, eventEmitter);
		}

		void handleMutationFailure({
			experienceTracker,
			experienceName: isInlineComment
				? DELETE_INLINE_COMMENT_EXPERIENCE
				: DELETE_PAGE_COMMENT_EXPERIENCE,
			error: err,
			commentId,
		});
	};

	const handleEditComment = () => {
		setCommentForEdit(commentId);
	};

	const exitCommentEditor = () => {
		setCommentForEdit('');
	};

	const handleReplyToComment = (id: string) => {
		setReplyToCommentId({
			parentCommentId: id,
			location: LocationValue.PANEL,
			user: {
				displayName: comment?.author?.displayName,
				accountId: (comment?.author as InlineCommentAuthorKnownUser)?.accountId,
			},
			contributorUsers: expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)
				? getReplyToContributorUsers(contributors)
				: undefined,
		});
	};

	const handleReopenMutationFailure = useCallback(
		({ isCacheUpdateFailed }: { isCacheUpdateFailed: boolean }) => {
			// TODO: Add experience tracking for reopening, which will allow us to differentiate and
			// detect cache errors vs mutation errors by the error value in the stopOnError() experience
			// call.  When that is added, we can go back to having a single flag message.
			void flags?.showErrorFlag({
				id: commentId,
				title: formatMessage(
					isCacheUpdateFailed
						? i18n.commentReopenCacheUpdateFailedFlagTitle
						: i18n.commentReopenFailedFlagTitle,
				),
				isAutoDismiss: true,
				actions: [
					{
						content: formatMessage(i18n.retryCTA),
						onClick: () => {
							void flags.hideFlag(flags.state.flags[0]?.id);
							handleReopenCommentRef.current?.();
						},
					},
				],
			});
		},
		[commentId, flags, formatMessage],
	);

	const reopenCommentUpdateFn: MutationUpdaterInterop<CommentReopenCommentMutation_Mutation$data> =
		useCallback(
			(_dataProxy, mutationResult) =>
				updateReopenCommentInCache({
					mutationResult,
					commentId,
					updateCallback: reopenCommentInCacheCallback,
					onError: () => handleReopenMutationFailure({ isCacheUpdateFailed: true }),
				}),
			[commentId, handleReopenMutationFailure, updateReopenCommentInCache],
		);

	const handleReopenComment = () => {
		// NOTE: The reopen experience is started in CommentActions.tsx
		reopenCommentFn({
			variables: { commentId, cloudId },
			update: reopenCommentUpdateFn,
		})
			.then(() => {
				handleReopenSuccess({
					commentId,
					parentCommentMarkerRef: threadKey,
					pageId: contentId,
					pageMode,
					source: AnalyticsSource.COMMENTS_PANEL,
					commentType,
					createAnalyticsEvent,
					isDangling: inlineCommentlocation?.inlineResolveProperties?.isDangling,
				});

				// Update the list of active annotationIds
				addUnresolvedInlineComment(threadKey, pageMode);

				// Emit the event to editor/renderer to reactivate the annotationId
				if (!isDatabaseOrWhiteboard && commentType === CommentType.INLINE) {
					if (expVal('confluence_fe_resolve_reopen_comment_refactor', 'isEnabled', false)) {
						unresolveInlineCommentAnnotation(threadKey, pageMode, eventEmitter);
					} else {
						if (pageMode === PageMode.VIEW) {
							const emitter = getRendererAnnotationEventEmitter();
							emitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_STATE, {
								[threadKey]: {
									id: threadKey,
									annotationType: AnnotationTypes.INLINE_COMMENT,
									state: AnnotationMarkStates.ACTIVE,
								},
							});
						} else {
							const emitter = getEditorAnnotationEventEmitter();
							emitter.emit('unresolve', threadKey);
						}
					}
				}

				// Show the reopen flag
				void flags?.showSuccessCircleFlag({
					id: commentId,
					title: formatMessage(i18n.commentReopenedFlagTitle),
					isAutoDismiss: true,
					actions: [
						{
							content: formatMessage(commentsI18n.goToCommentCTA),
							onClick: () => {
								const isDangling = inlineCommentlocation?.inlineResolveProperties?.isDangling;

								// Always call both `showCommentsPanel` and `setCurrentView`.
								// We can't reliably read `isCommentsPanelShown` here: this onClick is held by
								// the flags state container and the `Comment` component that registered it
								// is unmounted whenever the panel is closed, so any closure / ref over the
								// "panel shown" value is permanently stale by the time the user clicks
								// "Go to comment". `showCommentsPanel` is a no-op when the comments panel
								// is already shown (see `showObjectSidebar` in object-sidebar-api), and
								// `setCurrentView` is needed so that the view is updated when the panel
								// is already open. Together they handle both cases correctly.
								const currentViewValue =
									isDangling &&
									isNewDanglingCommentExperienceEnable &&
									!expValEquals('cc_comments_detached_in_open_view', 'isEnabled', true)
										? ViewValues.DELETED_CONTENT
										: ViewValues.OPEN;
								showCommentsPanel({ openWithView: currentViewValue });
								setCurrentView(currentViewValue);

								// Call navigation with view change waiting for reopen flow
								void handleCommentsPanelNavigationWithViewChange({
									scrollBehavior: ScrollBehavior.SMOOTH,
									context: NavigationContext.PANEL,
									block: 'center',
									annotationId: threadKey,
									commentType: commentType === CommentType.INLINE ? 'INLINE' : 'FOOTER',
									annotationClicked: true,
									expectedView: currentViewValue,
									commentId,
								});

								if (commentType === CommentType.INLINE || commentType === CommentType.GENERAL) {
									setCurrentlySelectedThreadKey(threadKey);
								}

								void flags.hideFlag(flags.state.flags[0]?.id);
							},
						},
						{
							content: formatMessage(commentsI18n.undoCTA),
							onClick: () => {
								handleResolveCommentRef.current?.();
								void flags.hideFlag(flags.state.flags[0]?.id);
							},
						},
					],
				});
				if (commentType === CommentType.INLINE) {
					if (isDatabaseOrWhiteboard && fg('cc-whiteboards-consolidating-comments')) {
						// add unresolved comment to whiteboard and database
						const commentInMap = commentsDataMap[commentType][threadKey];
						const commentLocation = commentInMap.location as CommentLocation;
						commentService.onReopenResolvedComment?.({
							contentId,
							referenceId: commentLocation.inlineMarkerRef!,
						});
					}

					const orderedActiveAnnotationIds = orderedActiveAnnotationIdList.map(
						(annotation) => annotation.threadKey,
					);

					// only insert reopened comment annotation if the comment highlight exists
					if (!(resolveProperties as CommentInlineResolveProperties)?.isDangling) {
						let annotationsList = annotations;
						if (pageMode === PageMode.VIEW) {
							const annotationMarks = rendererActions?.getAnnotationMarks();
							annotationsList = annotationMarks?.map((mark) => mark.attrs.id) ?? [];
						}

						if (annotationsList.length > 0) {
							insertReopenedCommentAnnotation(
								annotationsList,
								threadKey,
								orderedActiveAnnotationIds,
								setOrderedActiveAnnotationIdList,
							);
						}
					}
				}
			})
			.catch(() => {
				handleReopenMutationFailure({ isCacheUpdateFailed: false });
			});
	};

	const resolveExperienceName =
		commentType === CommentType.INLINE
			? RESOLVE_INLINE_COMMENT_EXPERIENCE
			: RESOLVE_PAGE_COMMENT_EXPERIENCE;

	const handleResolveMutationFailure = useCallback(
		(resolveError: Error) => {
			void handleMutationFailure({
				experienceTracker,
				experienceName: resolveExperienceName,
				error: resolveError,
			});
		},
		[experienceTracker, resolveExperienceName],
	);

	const resolveCommentsUpdateFn: MutationUpdaterInterop<CommentResolveCommentMutation_Mutation$data> =
		useCallback(
			(_dataProxy, mutationResult) =>
				updateResolveCommentInCache({
					mutationResult,
					commentId,
					updateCallback: resolveCommentInCacheCallback,
					onError: handleResolveMutationFailure,
				}),
			[updateResolveCommentInCache, commentId, handleResolveMutationFailure],
		);

	const handleResolveComment = () => {
		// Optimistically mark the inline comment annotation as resolved
		if (!isDatabaseOrWhiteboard && commentType === CommentType.INLINE) {
			resolveInlineCommentAnnotation(threadKey, pageMode, eventEmitter);
		}

		// NOTE: The RESOLVED experience is started in CommentActions.tsx
		resolveCommentFn({
			variables: { commentIds: [commentId], cloudId },
			update: resolveCommentsUpdateFn,
			// When a `optimisticResponse` is set, useMutation invokes the update function twice:
			// first immediately, and after mutation succeeds, a second time. If mutation fails,
			// the cache updated by the update function is rolled back.
			optimisticResponse: {
				confluence_resolveComments: {
					commentResolutionStates: [
						{
							commentId,
							status: true,
							resolveProperties: {
								// These 2 values will be updated to the correct value after mutation succeeds.
								// - `resolvedUser` is only displayed in comments panel Resolved view,
								//   inside a tooltip when hovered.
								// - `resolvedFriendlyDate` is not used by the current comments panel UI.
								// So setting it as empty optimistically won't affect the static UI.
								resolvedUser: null,
								resolvedFriendlyDate: '',
								resolvedTime: Date.now(),
								__typename: 'InlineCommentResolveProperties',
							},
							__typename: 'ConfluenceCommentResolutionState',
						},
					],
					success: true,
					errors: [],
					__typename: 'ConfluenceResolveCommentsPayload',
				},
				// typed as any because optimisticResponse is provided only for the resolve comment mutation
			} as CommentResolveCommentMutation_Mutation$data as any,
		})
			.then(() => {
				// Update content types comment service if we are resolving a comment
				commentService.onResolveComment?.(threadKey);

				if (commentType === CommentType.INLINE) {
					// This handles INLINE comment annotation. No need to invoke it for GENERAL comment.
					handleResolveSuccess({
						commentId,
						parentCommentMarkerRef: threadKey,
						pageId: contentId,
						pageMode,
						commentType,
						eventEmitter,
						source: AnalyticsSource.COMMENTS_PANEL,
						removeUnresolvedInlineComment,
						createAnalyticsEvent,
						setEditorViewProps,
						isDangling: inlineCommentlocation?.inlineResolveProperties?.isDangling,
						//getInlineNodeTypes  // TODO: Figure out how to get this info
					});
				}

				// Pages keep the active marker list to unresolved annotations, so the resolved thread
				// leaves it here. On slides the iframe owns the list instead: it reports every comment
				// still in the deck, resolved or not, and the Resolved view reads it under
				// `cc_comments_detached_slides_label` to tell detached threads from live ones.
				if (
					!(
						contentType === 'slide' &&
						expValEquals('cc_comments_detached_in_open_view', 'isEnabled', true) &&
						expValEquals('cc_comments_detached_slides_label', 'isEnabled', true)
					)
				) {
					setOrderedActiveAnnotationIdList(
						orderedActiveAnnotationIdList
							.map((status) => status.threadKey)
							.filter((id) => id !== threadKey),
					);
				}

				// Show the resolved flag
				void flags?.showSuccessCircleFlag({
					id: commentId,
					title: formatMessage(commentsI18n.resolvedFlagText),
					isAutoDismiss: true,
					actions: [
						{
							content: formatMessage(commentsI18n.goToCommentCTA),
							onClick: () => {
								// Always call both `showCommentsPanel` and
								// `setCurrentView`. We can't reliably read `isCommentsPanelShown`
								// here: this onClick is held by the flags state container, and the
								// `Comment` component that registered it is unmounted whenever the
								// panel is closed, so any closure / ref over the "panel shown" value
								// is permanently stale by the time the user clicks "Go to comment".
								// `showCommentsPanel` is a no-op when the comments panel is already
								// shown (see `showObjectSidebar` in object-sidebar-api), and
								// `setCurrentView` is needed so the view is updated when the panel
								// is already open. Together they handle both cases correctly.
								showCommentsPanel({ openWithView: ViewValues.RESOLVED });
								setCurrentView(ViewValues.RESOLVED);

								void handleCommentsPanelNavigationWithViewChange({
									scrollBehavior: ScrollBehavior.SMOOTH,
									context: NavigationContext.PANEL,
									block: 'center',
									annotationId: threadKey,
									commentType: commentType === CommentType.INLINE ? 'INLINE' : 'FOOTER',
									annotationClicked: true,
									expectedView: ViewValues.RESOLVED,
									commentId,
								});

								void flags.hideFlag(flags.state.flags[0]?.id);
							},
						},
						{
							content: formatMessage(commentsI18n.undoCTA),
							onClick: () => {
								// Always re-open the panel to the OPEN view.
								// `showCommentsPanel` is a no-op when the panel is already shown
								// (see `showObjectSidebar` in object-sidebar-api). We can't reliably
								// read `isCommentsPanelShown` here because the `Comment` component
								// that registered this onClick is unmounted whenever the panel is
								// closed, so any closure / ref over the "panel shown" value is
								// permanently stale.
								const isDangling = inlineCommentlocation?.inlineResolveProperties?.isDangling;
								const currentViewValue =
									isDangling &&
									isNewDanglingCommentExperienceEnable &&
									!expValEquals('cc_comments_detached_in_open_view', 'isEnabled', true)
										? ViewValues.DELETED_CONTENT
										: ViewValues.OPEN;
								showCommentsPanel({ openWithView: currentViewValue });

								handleReopenComment();
								void flags.hideFlag(flags.state.flags[0]?.id);
							},
						},
					],
				});

				experienceTracker.succeed({
					name: resolveExperienceName,
				});
			})
			.catch((err) => {
				// Undo resolving the inline comment annotation
				if (!isDatabaseOrWhiteboard && commentType === CommentType.INLINE) {
					unresolveInlineCommentAnnotation(threadKey, pageMode, eventEmitter);
				}

				// Show an error flag to user when resolve fails
				void flags?.showErrorFlag({
					id: commentId,
					title: formatMessage(commentsI18n.resolveErrorTitle),
					description: formatMessage(commentsI18n.resolveError),
				});

				handleResolveMutationFailure(err);
			});
	};

	// Update refs to break circular dependency
	handleResolveCommentRef.current = handleResolveComment;
	handleReopenCommentRef.current = handleReopenComment;

	const pageType = pageInfo?.type ?? '';

	// Editor setup
	const spaceId = pageInfo?.space?.id ?? '';

	// User page permissions
	const operations = pageInfo?.operations || [];

	// User space permissions - only access when feature gate is on
	const spaceOperations = fg('confluence_comments_space_operations_permissions')
		? pageInfo?.space?.operations || []
		: [];

	const canAddComment = operations.some(
		(op) => op?.operation === 'create' && op?.targetType === 'comment',
	);

	const canAddAttachment =
		operations.some((op) => op?.operation === 'create' && op?.targetType === 'attachment') ||
		spaceOperations.some((op) => op?.operation === 'create' && op?.targetType === 'attachment');

	const hasMediaUploadPermissions = canAddComment && canAddAttachment;

	// We only want to focus resolved comments because the 'active' comments have
	// their own treatment for when one is hovered/selected and could cause weird behavior
	const isCommentFocused = resolveProperties?.resolved && commentId === focusedCommentId;

	const shouldShowLeftBorder =
		commentHandlesPadding && commentId === editCommentId && isReply && !isLastUnnestableReply;

	const handleStandaloneReply = (id: string) => {
		replyToComment && replyToComment(id, comment);
	};

	return (
		<div
			css={[
				commentHandlesPadding && commentWrapperReplyPaddingOverrides,
				commentHandlesPadding &&
					isStandaloneComment &&
					standaloneCommentWrapperReplyPaddingOverrides,
				shouldShowLeftBorder && leftBorderStyle,
				shouldShowLeftBorder && fg('confluence_comment_border_migration') && leftBorderStyleUpdated,
			]}
		>
			{commentId === editCommentId ? (
				<EditComment
					pageId={contentId}
					pageType={pageType}
					annotationId={threadKey}
					commentId={commentId}
					spaceId={spaceId}
					content={content ?? ''}
					isReply={isReply}
					displayCommentInViewMode={exitCommentEditor}
					avatarUrl={topCommentUserAvatar ?? ''}
					displayName={topCommentDisplayName}
					date={date ?? ''}
					dateUrl={commentUrl ?? ''}
					createdAtNonLocalized={createdAtNonLocalized ?? ''}
					versionByDisplayName={versionByDisplayName}
					versionDate={versionDate ?? ''}
					resolveProperties={resolveProperties}
					isDangling={isThreadRootDangling ?? false}
					mode={pageMode === PageMode.VIEW ? 'view' : 'edit'}
					hasMediaUploadPermissions={hasMediaUploadPermissions}
					isCommentsPanel={isCommentsPanelShown}
					commentType={commentType}
					isStandaloneComment={isStandaloneComment}
					isOffline={isOffline}
					isLastUnnestableReply={isLastUnnestableReply}
					hasParentMaxHeight={hasParentMaxHeight}
					numReplies={numReplies}
				/>
			) : (
				<CommentBody
					mode="view"
					userId={userId}
					avatarUrl={topCommentUserAvatar}
					displayName={topCommentDisplayName}
					date={date ?? ''}
					dateUrl={commentUrl ?? ''}
					createdAt={createdAt ?? ''}
					createdAtNonLocalized={createdAtNonLocalized ?? ''}
					versionByDisplayName={versionByDisplayName}
					versionDate={versionDate ?? ''}
					pageId={contentId}
					pageType={pageType}
					commentId={commentId ?? ''}
					agentChatMenuItem={
						expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true) &&
						expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true) ? (
							<CommentAgentOpenChatMenuItemAsync commentId={commentId ?? ''} />
						) : undefined
					}
					isReply={isReply}
					isEdited={isEdited}
					parentCommentId={parentCommentId}
					permissions={permissions}
					content={content ?? ''}
					permissionType={(permissionType as any) ?? undefined}
					supportedActions={supportedTopLevelActions}
					deleteComment={
						commentType === CommentType.INLINE
							? handleDeleteInlineComment
							: handleDeleteGeneralComment
					}
					editComment={handleEditComment}
					resolveComment={handleResolveComment}
					numReplies={numReplies}
					isUnread={isUnread}
					isCommentActive
					isCommentsPanel
					isFocused={isCommentFocused}
					isOffline={isOffline}
					resolveProperties={resolveProperties}
					annotationId={threadKey}
					annotatedText={annotatedText}
					reopenComment={handleReopenComment}
					replyToComment={isStandaloneComment ? handleStandaloneReply : handleReplyToComment}
					inheritedReactionsData={inheritedReactionsData}
					reactionsReadOnly={
						Boolean(commentId?.startsWith(OPTIMISTIC_AGENT_REPLY_ID_PREFIX)) &&
						expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
					}
					currentlySelectedThreadKey={currentlySelectedThreadKey}
					setCurrentlySelectedThreadKey={setCurrentlySelectedThreadKey}
					isAnnotatedTextPressed={isAnnotatedTextPressed}
					setAnnotatedTextPressed={setAnnotatedTextPressed}
					commentType={commentType}
					adjustForNestedReplies={adjustForNestedReplies}
					// Extend the reply's connector line to span the agent status row below it (rendered
					// outside CommentBody's measured height) so the line reaches the next reply. Only
					// when this is NOT the last reply — otherwise the line would dangle past the status
					// row into nothing. The 48px is tunable (approx. status-row height). Gate inlined, last.
					// Also extend it for the LAST reply when the reply editor is open below it, so the
					// line spans the status row down to the editor instead of stopping short (gap fix).
					extraReplyBranchHeight={
						isReply &&
						(!isLastUnnestableReply || isReplyEditorOpen) &&
						!!agentSessionState &&
						expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)
							? 48
							: 0
					}
					hideBranchingStyle={hideBranchingStyle}
					canAddComments={canAddComments}
					isParentCommentOpen={isParentCommentOpen}
					reactionsCacheUpdateFn={updateReactionsCacheFn}
					isStandaloneComment={isStandaloneComment}
					showRepliesWithCurvedBorders={showRepliesWithCurvedBorders}
					onRendered={onRendered}
					contributors={contributors}
				/>
			)}
			{/* When this comment (top-level or reply) has an associated agent session, attach
			    the AvatarWithActionButton BELOW the comment — the comment still renders normally.
			    Gate inlined and ordered last per confluence-feature-gating rules. */}
			{agentSessionState &&
				expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true) &&
				(isReply ? (
					// Nested replies: wrap the status row in the same curved branch a reply uses so it
					// attaches to the thread's connector line. Top-level comments render it unwrapped.
					<WithCurvedBranchingStyle
						showDefaultStyles
						customStyles={{ left: '-16px', height: '24px' }}
					>
						<AvatarWithActionButtonAsync
							commentId={commentId}
							agentState={agentSessionState}
							agentIdentity={agentSession ?? optimisticAgentIdentity}
							conversationId={agentSession?.conversationId ?? ''}
							isReply
							userId={agentSession?.agentId ?? userId}
							avatarUrl={agentSession?.avatarUrl ?? topCommentUserAvatar}
							agentName={agentSession?.agentName ?? undefined}
							sessionStatusRef={sessionStatusRef}
						/>
					</WithCurvedBranchingStyle>
				) : (
					<AvatarWithActionButtonAsync
						commentId={commentId}
						agentState={agentSessionState}
						agentIdentity={agentSession ?? optimisticAgentIdentity}
						conversationId={agentSession?.conversationId ?? ''}
						userId={agentSession?.agentId ?? userId}
						avatarUrl={agentSession?.avatarUrl ?? topCommentUserAvatar}
						agentName={agentSession?.agentName ?? undefined}
						sessionStatusRef={sessionStatusRef}
						threadRail={
							drawsThreadRail({ hideBranchingStyle, numReplies, canAddComments }) &&
							expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
								? 'comment'
								: undefined
						}
					/>
				))}
		</div>
	);
};

// Shallow-equal props skip re-rendering the heavy Comment subtree
// (CommentBody, EditComment, mutations) during unrelated parent renders.
export const Comment: typeof CommentComponent = memo(
	CommentComponent,
	arePropsShallowEqual,
) as typeof CommentComponent;
